(() => {
  const initialized = new WeakSet();
  const mobileQuery = window.matchMedia('(max-width: 749px)');
  let lockedScrollY = 0;

  function lockPage() {
    lockedScrollY = window.scrollY || window.pageYOffset || 0;
    document.documentElement.classList.add('rtcg-card-lightbox-open');
    document.body.classList.add('rtcg-card-lightbox-open');
    document.body.style.top = `-${lockedScrollY}px`;
  }

  function unlockPage() {
    document.documentElement.classList.remove('rtcg-card-lightbox-open');
    document.body.classList.remove('rtcg-card-lightbox-open');
    document.body.style.top = '';
    window.scrollTo(0, lockedScrollY);
  }

  function initGallery(gallery) {
    if (!gallery || initialized.has(gallery)) return;
    initialized.add(gallery);

    const thumbs = Array.from(gallery.querySelectorAll('[data-rtcg-card-thumb]'));
    const slides = Array.from(gallery.querySelectorAll('[data-rtcg-card-slide]'));
    const track = gallery.querySelector('.rtcg-card-gallery__slides');
    let lightbox = gallery.querySelector('[data-rtcg-card-lightbox]');

    if (lightbox && lightbox.parentElement !== document.body) {
      document.body.appendChild(lightbox);
    }

    const lightboxImage = lightbox ? lightbox.querySelector('[data-rtcg-card-lightbox-image]') : null;
    const closeButton = lightbox ? lightbox.querySelector('[data-rtcg-card-close]') : null;
    let lastOpener = null;
    let activeIndex = 0;
    let scrollFrame = 0;
    let pointerStartScrollLeft = 0;

    function setActiveState(index) {
      const targetIndex = slides.length ? Math.min(slides.length - 1, Math.max(0, Number(index) || 0)) : 0;
      activeIndex = targetIndex;

      slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === targetIndex;
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        const opener = slide.querySelector('[data-rtcg-card-open]');
        if (opener) opener.tabIndex = isActive ? 0 : -1;
      });

      thumbs.forEach((thumb, thumbIndex) => {
        const isActive = thumbIndex === targetIndex;
        thumb.classList.toggle('is-active', isActive);
        thumb.setAttribute('aria-current', isActive ? 'true' : 'false');
      });
    }

    function show(index, { scroll = true, behavior = 'smooth' } = {}) {
      const targetIndex = slides.length ? (Number(index) + slides.length) % slides.length : 0;
      setActiveState(targetIndex);

      if (scroll && mobileQuery.matches && track && slides[targetIndex]) {
        track.scrollTo({ left: slides[targetIndex].offsetLeft, behavior });
      }
    }

    function syncActiveFromScroll() {
      if (!mobileQuery.matches || !track || slides.length < 2) return;
      cancelAnimationFrame(scrollFrame);
      scrollFrame = requestAnimationFrame(() => {
        const viewportCenter = track.scrollLeft + track.clientWidth / 2;
        let nearestIndex = activeIndex;
        let nearestDistance = Number.POSITIVE_INFINITY;

        slides.forEach((slide, index) => {
          const slideCenter = slide.offsetLeft + slide.offsetWidth / 2;
          const distance = Math.abs(slideCenter - viewportCenter);
          if (distance < nearestDistance) {
            nearestDistance = distance;
            nearestIndex = index;
          }
        });

        if (nearestIndex !== activeIndex) setActiveState(nearestIndex);
      });
    }

    function normalizeForViewport() {
      if (!track || !slides.length) return;
      setActiveState(activeIndex);
      if (mobileQuery.matches) {
        track.scrollTo({ left: slides[activeIndex].offsetLeft, behavior: 'auto' });
      } else {
        track.scrollLeft = 0;
      }
    }

    function openLightbox(button) {
      if (!lightbox || !lightboxImage) {
        const src = button.dataset.rtcgFullSrc;
        if (src) window.open(src, '_blank', 'noopener');
        return;
      }

      lastOpener = button;
      lightboxImage.src = button.dataset.rtcgFullSrc || '';
      lightboxImage.alt = button.dataset.rtcgAlt || '';
      lightbox.hidden = false;
      lightbox.classList.add('is-open');
      lockPage();

      requestAnimationFrame(() => closeButton?.focus({ preventScroll: true }));
    }

    function closeLightbox() {
      if (!lightbox || lightbox.hidden) return;

      lightbox.classList.remove('is-open');
      lightbox.hidden = true;
      unlockPage();

      window.setTimeout(() => {
        if (lightbox.hidden && lightboxImage) {
          lightboxImage.removeAttribute('src');
          lightboxImage.alt = '';
        }
      }, 80);

      if (lastOpener?.isConnected && typeof lastOpener.focus === 'function') {
        lastOpener.focus({ preventScroll: true });
      }
    }

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => show(thumb.dataset.rtcgCardIndex));
    });

    gallery.querySelectorAll('[data-rtcg-card-open]').forEach((button) => {
      button.addEventListener('pointerdown', () => {
        pointerStartScrollLeft = track?.scrollLeft || 0;
      });

      button.addEventListener('click', (event) => {
        // A native horizontal pan can end over the same button. Treat any
        // meaningful track movement as a swipe, never as a lightbox click.
        if (mobileQuery.matches && track && Math.abs(track.scrollLeft - pointerStartScrollLeft) > 6) {
          event.preventDefault();
          return;
        }
        openLightbox(button);
      });
    });

    if (track) track.addEventListener('scroll', syncActiveFromScroll, { passive: true });
    if (typeof mobileQuery.addEventListener === 'function') {
      mobileQuery.addEventListener('change', normalizeForViewport);
    } else if (typeof mobileQuery.addListener === 'function') {
      mobileQuery.addListener(normalizeForViewport);
    }

    if (closeButton) {
      closeButton.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        closeLightbox();
      });
    }

    if (lightbox) {
      lightbox.addEventListener('click', (event) => {
        if (event.target.closest('[data-rtcg-card-close]')) {
          event.preventDefault();
          closeLightbox();
          return;
        }
        if (event.target === lightbox) closeLightbox();
      });
    }

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && lightbox && !lightbox.hidden) closeLightbox();
    });

    normalizeForViewport();
  }

  function initAll(root = document) {
    root.querySelectorAll('[data-rtcg-card-gallery]').forEach(initGallery);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
